import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Settings as SettingsIcon, Upload, Save, UserCheck, UserX, Clock, Folder, Plus, X, Loader2, Image as ImageIcon, Mail, Copy, Check, Edit, UserPlus } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useSiteSettings } from '../App';

import { getBackendUrl } from '../utils/getBackendUrl';
import { AdminNotificationPanel } from '../components/AdminNotificationPanel';
import { NotificationSettings } from '../components/NotificationSettings';
import { NotificationEmailSettings } from '../components/NotificationEmailSettings';
import { PhotoViewAlertSettings } from '../components/PhotoViewAlertSettings';
import { CreateSupervisorForm } from '../components/CreateSupervisorForm';
import { LoginSoundSettings } from '../components/LoginSoundSettings';
import { AdminFamilyInvites } from '../components/AdminFamilyInvites';
const BACKEND_URL = getBackendUrl();
const API = `${BACKEND_URL}/api`;

const Settings = ({ user }) => {
  const { siteSettings, fetchSiteSettings } = useSiteSettings();
  const [newTitle, setNewTitle] = useState(siteSettings.site_title || '');
  const [newSubtitle, setNewSubtitle] = useState(siteSettings.site_subtitle || '');
  const [backgroundColor, setBackgroundColor] = useState(siteSettings.background_color || '#f9fafb');
  const [logoFile, setLogoFile] = useState(null);
  const [loginBgFile, setLoginBgFile] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [pendingUsers, setPendingUsers] = useState([]);
  const [activeTab, setActiveTab] = useState(() => {
    const tab = new URLSearchParams(window.location.search).get('tab');
    return ['site', 'users', 'categories', 'service-types', 'permissions', 'invitations', 'notifications', 'splash'].includes(tab) ? tab : 'site';
  });
  const [highlightUserId] = useState(() => new URLSearchParams(window.location.search).get('highlight'));
  const [splashImageFile, setSplashImageFile] = useState(null);
  const [currentSplashImage, setCurrentSplashImage] = useState(null);
  const [splashMessage, setSplashMessage] = useState('');
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [categories, setCategories] = useState([]);
  const [serviceTypes, setServiceTypes] = useState([]);
  const [newServiceType, setNewServiceType] = useState('');
  const [newCategory, setNewCategory] = useState('');
  
  // Permissions states
  const [allUsers, setAllUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userPermissions, setUserPermissions] = useState(null);
  
  // Invitation states
  const [invitations, setInvitations] = useState([]);
  const [invitationMessage, setInvitationMessage] = useState('');
  const [invitationEmails, setInvitationEmails] = useState('');
  const [isCopied, setIsCopied] = useState(false);
  const [copiedType, setCopiedType] = useState(''); // 'html', 'text', or 'sms'
  const [isGenerating, setIsGenerating] = useState(false);

  useEffect(() => {
    if (user.role !== 'admin') {
      toast.error('Access denied. Admin only.');
      return;
    }
  }, [user]);

  useEffect(() => {
    // Only update if there are no unsaved changes
    if (!hasUnsavedChanges) {
      setNewTitle(siteSettings.site_title || 'BuildTrack');
      setNewSubtitle(siteSettings.site_subtitle || 'Construction Project Management');
      setBackgroundColor(siteSettings.background_color || '#f9fafb');
    }
  }, [siteSettings, hasUnsavedChanges]);

  useEffect(() => {
    if (activeTab === 'users') {
      fetchPendingUsers();
    } else if (activeTab === 'categories') {
      fetchCategories();
    } else if (activeTab === 'service-types') {
      fetchServiceTypes();
    } else if (activeTab === 'permissions') {
      fetchAllUsersForPermissions();
    } else if (activeTab === 'invitations') {
      fetchInvitations();
      loadInvitationMessage();
    } else if (activeTab === 'splash') {
      fetchSplashSettings();
    }
  }, [activeTab]);

  useEffect(() => {
    if (highlightUserId && pendingUsers.length > 0) {
      const t = setTimeout(() => {
        const el = document.getElementById(`pending-user-${highlightUserId}`);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 300);
      return () => clearTimeout(t);
    }
  }, [highlightUserId, pendingUsers]);


  const fetchPendingUsers = async () => {
    try {
      const response = await axios.get(`${API}/admin/pending-users`);
      if (Array.isArray(response.data)) {
        setPendingUsers(response.data);
      } else {
        setPendingUsers([]);
      }
    } catch (error) {
      console.error('Failed to fetch pending users:', error);
      setPendingUsers([]);
    }
  };

  const handleApproveUser = async (userId) => {
    try {
      await axios.post(`${API}/admin/approve-user/${userId}`);
      toast.success('User approved successfully!');
      fetchPendingUsers();
    } catch (error) {
      toast.error('Failed to approve user');
    }
  };

  const handleRejectUser = async (userId) => {
    try {
      await axios.post(`${API}/admin/reject-user/${userId}`);
      toast.success('User rejected');
      fetchPendingUsers();
    } catch (error) {
      toast.error('Failed to reject user');
    }
  };

  const fetchSplashSettings = async () => {
    try {
      const response = await axios.get(`${API}/splash-screen-settings`);
      if (response.data.splash_image) {
        setCurrentSplashImage(response.data.splash_image);
      }
      if (response.data.message !== undefined) {
        setSplashMessage(response.data.message);
      }
    } catch (error) {
      console.error('Failed to fetch splash settings:', error);
    }
  };


  const handleUpdateSettings = async () => {
    if (!newTitle.trim()) {
      toast.error('Site title cannot be empty');
      return;
    }

    setIsLoading(true);
    try {
      const response = await axios.put(`${API}/settings/site`, {
        site_title: newTitle,
        site_subtitle: newSubtitle,
        background_color: backgroundColor
      });
      
      // Apply background color immediately from the response
      if (response.data.background_color) {
        document.body.style.backgroundColor = response.data.background_color;
      }
      
      toast.success('Settings updated successfully!');
      
      // Reset unsaved changes flag FIRST to prevent race condition
      setHasUnsavedChanges(false);
      
      // Then update parent context - useEffect will handle updating local state
      await fetchSiteSettings();
    } catch (error) {
      toast.error('Failed to update settings');
      console.error('Settings update error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleUploadLogo = async () => {
    if (!logoFile) {
      toast.error('Please select a logo file');
      return;
    }

    const formData = new FormData();
    formData.append('file', logoFile);

    setIsUploading(true);
    try {
      const response = await axios.post(`${API}/settings/site/upload-logo`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      toast.success('Logo uploaded successfully!');
      console.log('Upload response:', response.data);
      
      setLogoFile(null);
      
      // Wait a bit to ensure file is saved, then refresh
      await new Promise(resolve => setTimeout(resolve, 500));
      await fetchSiteSettings();
      
      // Force a hard refresh of the logo image
      const timestamp = new Date().getTime();
      const logoElements = document.querySelectorAll('img[alt="Site logo"], img[alt="Preview"]');
      logoElements.forEach(img => {
        if (img.src.includes('/api/uploads/')) {
          img.src = img.src.split('?')[0] + '?t=' + timestamp;
        }
      });
      
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(error.response?.data?.detail || 'Failed to upload logo');
    } finally {
      setIsUploading(false);
    }
  };

  const handleUploadLoginBackground = async () => {
    if (!loginBgFile) {
      toast.error('Please select a background image file');
      return;
    }

    const formData = new FormData();
    formData.append('file', loginBgFile);

    setIsUploading(true);
    try {
      const response = await axios.post(`${API}/settings/site/upload-login-background`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      toast.success('Login background uploaded successfully!');
      console.log('Upload response:', response.data);
      
      setLoginBgFile(null);
      
      // Wait a bit to ensure file is saved, then refresh
      await new Promise(resolve => setTimeout(resolve, 500));
      await fetchSiteSettings();
      
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(error.response?.data?.detail || 'Failed to upload login background');
    } finally {
      setIsUploading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/settings/categories`);
      if (response.data && Array.isArray(response.data.categories)) {
        setCategories(response.data.categories);
      } else {
        setCategories([]);
      }
    } catch (error) {
      console.error('Failed to fetch categories:', error);
      setCategories([]);
    }
  };

  const handleAddCategory = () => {
    if (newCategory.trim() && !categories.includes(newCategory.trim())) {
      setCategories([...categories, newCategory.trim()]);
      setNewCategory('');
    }
  };

  const handleRemoveCategory = (category) => {
    setCategories(categories.filter(c => c !== category));
  };

  const handleSaveCategories = async () => {
    setIsLoading(true);
    try {
      await axios.put(`${API}/settings/categories`, { categories });
      toast.success('Categories updated successfully!');
    } catch (error) {
      toast.error('Failed to update categories');
    } finally {
      setIsLoading(false);
    }
  };

  // Service Types functions
  const fetchServiceTypes = async () => {
    try {
      const response = await axios.get(`${API}/settings/service-types`);
      if (response.data && Array.isArray(response.data.service_types)) {
        setServiceTypes(response.data.service_types);
      } else {
        setServiceTypes([]);
      }
    } catch (error) {
      console.error('Failed to fetch service types:', error);
      setServiceTypes([]);
    }
  };

  const handleAddServiceType = () => {
    if (newServiceType.trim() && !serviceTypes.includes(newServiceType.trim())) {
      setServiceTypes([...serviceTypes, newServiceType.trim()]);
      setNewServiceType('');
    } else if (serviceTypes.includes(newServiceType.trim())) {
      toast.error('This service type already exists');
    }
  };

  const handleRemoveServiceType = (serviceType) => {
    setServiceTypes(serviceTypes.filter(st => st !== serviceType));
  };

  const handleSaveServiceTypes = async () => {
    if (serviceTypes.length === 0) {
      toast.error('Please add at least one service type');
      return;
    }
    setIsLoading(true);
    try {
      await axios.put(`${API}/settings/service-types`, { service_types: serviceTypes });
      toast.success('Service types updated successfully!');
    } catch (error) {
      toast.error('Failed to update service types');
    } finally {
      setIsLoading(false);
    }
  };

  // Permissions functions
  const fetchAllUsersForPermissions = async () => {
    try {
      const response = await axios.get(`${API}/users`);
      setAllUsers(response.data.filter(u => u.id !== user.id)); // Exclude current admin
      setSelectedUser(null);
      setUserPermissions(null);
    } catch (error) {
      toast.error('Failed to fetch users');
    }
  };

  const fetchUserPermissions = async (userId) => {
    try {
      const response = await axios.get(`${API}/admin/users/${userId}/permissions`);
      setUserPermissions(response.data.permissions);
      setSelectedUser(allUsers.find(u => u.id === userId));
    } catch (error) {
      toast.error('Failed to fetch permissions');
    }
  };

  const handlePermissionChange = (category, action, value) => {
    setUserPermissions(prev => ({
      ...prev,
      [category]: {
        ...prev[category],
        [action]: value
      }
    }));
  };

  const handleSavePermissions = async () => {
    if (!selectedUser) return;
    
    setIsLoading(true);
    try {
      await axios.put(`${API}/admin/users/${selectedUser.id}/permissions`, userPermissions);
      toast.success('Permissions updated successfully!');
    } catch (error) {
      toast.error('Failed to update permissions');
    } finally {
      setIsLoading(false);
    }
  };


  // Invitation functions
  const fetchInvitations = async () => {
    try {
      const response = await axios.get(`${API}/invitations`);
      setInvitations(response.data);
    } catch (error) {
      console.error('Failed to fetch invitations:', error);
      toast.error('Failed to load invitations');
    }
  };

  const loadInvitationMessage = async () => {
    try {
      const response = await axios.get(`${API}/settings/site`);
      setInvitationMessage(response.data.invitation_message || '');
    } catch (error) {
      console.error('Failed to load invitation message:', error);
    }
  };

  const handleSaveInvitationMessage = async () => {
    setIsLoading(true);
    try {
      await axios.put(`${API}/settings/invitation-message`, { invitation_message: invitationMessage });
      toast.success('Invitation message updated!');
    } catch (error) {
      toast.error('Failed to update invitation message');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateInvitations = async () => {
    const emailArray = invitationEmails
      .split('\n')
      .map(e => e.trim())
      .filter(e => e && e.includes('@'));

    if (emailArray.length === 0) {
      toast.error('Please enter at least one valid email address');
      return;
    }

    setIsGenerating(true);
    try {
      await axios.post(`${API}/invitations`, { emails: emailArray });
      toast.success(`${emailArray.length} invitation(s) created!`);
      setInvitationEmails('');
      fetchInvitations();
    } catch (error) {
      toast.error('Failed to create invitations');
    } finally {
      setIsGenerating(false);
    }
  };

  const generateInvitationHTML = () => {
    const registrationUrl = `${window.location.origin}/`;
    const logoUrl = siteSettings.site_logo ? `${BACKEND_URL}${siteSettings.site_logo}` : '';
    const companyName = siteSettings.site_title || 'BuildTrack';

    return `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Invitation from ${companyName}</title>
</head>
<body style="margin: 0; padding: 0; font-family: Arial, sans-serif; background-color: #f3f4f6;">
  <table width="100%" cellpadding="0" cellspacing="0" style="background-color: #f3f4f6; padding: 40px 20px;">
    <tr>
      <td align="center">
        <table width="600" cellpadding="0" cellspacing="0" style="background-color: #ffffff; border-radius: 8px; box-shadow: 0 2px 8px rgba(0,0,0,0.1);">
          <!-- Header with Logo -->
          <tr>
            <td style="padding: 40px 40px 20px; text-align: center; background-color: #2563eb; border-radius: 8px 8px 0 0;">
              ${logoUrl ? `<img src="${logoUrl}" alt="${companyName}" style="max-width: 150px; height: auto; margin-bottom: 20px;">` : ''}
              <h1 style="color: #ffffff; margin: 0; font-size: 28px; font-weight: bold;">${companyName}</h1>
            </td>
          </tr>
          
          <!-- Message -->
          <tr>
            <td style="padding: 40px;">
              <p style="color: #374151; font-size: 16px; line-height: 1.6; margin-bottom: 20px;">
                ${invitationMessage.replace(/\n/g, '<br>')}
              </p>
              
              <!-- CTA Button -->
              <table width="100%" cellpadding="0" cellspacing="0" style="margin: 30px 0;">
                <tr>
                  <td align="center">
                    <a href="${registrationUrl}" style="display: inline-block; background-color: #ea580c; color: #ffffff; text-decoration: none; padding: 14px 40px; border-radius: 6px; font-weight: bold; font-size: 16px;">Sign Up Now</a>
                  </td>
                </tr>
              </table>
              
              <p style="color: #6b7280; font-size: 14px; line-height: 1.6; margin-top: 30px;">
                Or copy and paste this link in your browser:<br>
                <a href="${registrationUrl}" style="color: #2563eb; word-break: break-all;">${registrationUrl}</a>
              </p>
            </td>
          </tr>
          
          <!-- Footer -->
          <tr>
            <td style="padding: 20px 40px; background-color: #f9fafb; border-radius: 0 0 8px 8px; text-align: center;">
              <p style="color: #6b7280; font-size: 14px; margin: 0;">
                © ${new Date().getFullYear()} ${companyName}. All rights reserved.
              </p>
            </td>
          </tr>
        </table>
      </td>
    </tr>
  </table>
</body>
</html>`;
  };

  const generatePlainTextInvitation = () => {
    const registrationUrl = `${window.location.origin}/`;
    const companyName = siteSettings.site_title || 'BuildTrack';

    return `${companyName}

${invitationMessage}

Sign up here: ${registrationUrl}

© ${new Date().getFullYear()} ${companyName}. All rights reserved.`;
  };

  const generateSMSInvitation = () => {
    const registrationUrl = `${window.location.origin}/`;
    const companyName = siteSettings.site_title || 'BuildTrack';

    return `${companyName} invites you to our new mobile platform! Stay updated on your project in real-time. Sign up free: ${registrationUrl}`;
  };

  const handleCopyInvitation = async () => {
    try {
      const html = generateInvitationHTML();
      await navigator.clipboard.writeText(html);
      setCopiedType('html');
      setIsCopied(true);
      toast.success('Invitation HTML copied to clipboard!');
      setTimeout(() => {
        setIsCopied(false);
        setCopiedType('');
      }, 2000);
    } catch (error) {
      toast.error('Failed to copy to clipboard');
    }
  };

  const handleCopyPlainText = async () => {
    try {
      const plainText = generatePlainTextInvitation();
      await navigator.clipboard.writeText(plainText);
      setCopiedType('text');
      setIsCopied(true);
      toast.success('Plain text invitation copied!');
      setTimeout(() => {
        setIsCopied(false);
        setCopiedType('');
      }, 2000);
    } catch (error) {
      toast.error('Failed to copy to clipboard');
    }
  };

  const handleCopySMS = async () => {
    try {
      const smsText = generateSMSInvitation();
      await navigator.clipboard.writeText(smsText);
      setCopiedType('sms');
      setIsCopied(true);
      toast.success('SMS text copied!');
      setTimeout(() => {
        setIsCopied(false);
        setCopiedType('');
      }, 2000);
    } catch (error) {
      toast.error('Failed to copy to clipboard');
    }
  };

  if (user.role !== 'admin') {
    return (
      <div className="page-container">
        <div className="glass-card p-8 text-center">
          <p className="text-red-400 text-lg">Access denied. This page is only accessible to administrators.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container pb-24 sm:pb-8" data-testid="settings-page">
      <div className="mb-6 sm:mb-8">
        <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-2">Settings</h1>
        <p className="text-sm sm:text-base text-slate-400">Manage site settings and user approvals</p>
      </div>

      {/* Tabs - Wrap to multiple rows */}
      <div className="flex flex-wrap gap-2 sm:gap-3 mb-6 pb-2">
        <Button
          onClick={() => setActiveTab('site')}
          className={`min-h-[44px] text-xs sm:text-sm px-3 sm:px-4  ${activeTab === 'site' ? 'bg-orange-500' : 'bg-slate-700'}`}
        >
          <SettingsIcon className="w-4 h-4 mr-1 sm:mr-2 flex-shrink-0" />
          <span className="whitespace-nowrap">Site Settings</span>
        </Button>
        <Button
          onClick={() => setActiveTab('categories')}
          className={`min-h-[44px] text-xs sm:text-sm px-3 sm:px-4  ${activeTab === 'categories' ? 'bg-orange-500' : 'bg-slate-700'}`}
        >
          <Folder className="w-4 h-4 mr-1 sm:mr-2 flex-shrink-0" />
          <span className="whitespace-nowrap">Categories</span>
        </Button>
        <Button
          onClick={() => setActiveTab('service-types')}
          className={`min-h-[44px] text-xs sm:text-sm px-3 sm:px-4  ${activeTab === 'service-types' ? 'bg-orange-500' : 'bg-slate-700'}`}
        >
          <SettingsIcon className="w-4 h-4 mr-1 sm:mr-2 flex-shrink-0" />
          <span className="whitespace-nowrap">Service Types</span>
        </Button>
        <Button
          onClick={() => setActiveTab('permissions')}
          className={`min-h-[44px] text-xs sm:text-sm px-3 sm:px-4  ${activeTab === 'permissions' ? 'bg-orange-500' : 'bg-slate-700'}`}
        >
          <UserCheck className="w-4 h-4 mr-1 sm:mr-2 flex-shrink-0" />
          <span className="whitespace-nowrap">Permissions</span>
        </Button>
        <Button
          onClick={() => setActiveTab('users')}
          className={`min-h-[44px] text-xs sm:text-sm px-3 sm:px-4  ${activeTab === 'users' ? 'bg-orange-500' : 'bg-slate-700'}`}
        >
          <Clock className="w-4 h-4 mr-1 sm:mr-2 flex-shrink-0" />
          <span className="whitespace-nowrap">Users ({pendingUsers.length})</span>
        </Button>
        <Button
          onClick={() => setActiveTab('invitations')}
          className={`min-h-[44px] text-xs sm:text-sm px-3 sm:px-4  ${activeTab === 'invitations' ? 'bg-orange-500' : 'bg-slate-700'}`}
        >
          <Mail className="w-4 h-4 mr-1 sm:mr-2 flex-shrink-0" />
          <span className="whitespace-nowrap">Invitations</span>
        </Button>
        <Button
          onClick={() => setActiveTab('splash')}
          className={`min-h-[44px] text-xs sm:text-sm px-3 sm:px-4  ${activeTab === 'splash' ? 'bg-orange-500' : 'bg-slate-700'}`}
        >
          <ImageIcon className="w-4 h-4 mr-1 sm:mr-2 flex-shrink-0" />
          <span className="whitespace-nowrap">Splash Screen</span>
        </Button>
        <Button
          onClick={() => setActiveTab('notifications')}
          className={`min-h-[44px] text-xs sm:text-sm px-3 sm:px-4  ${activeTab === 'notifications' ? 'bg-orange-500' : 'bg-slate-700'}`}
        >
          <SettingsIcon className="w-4 h-4 mr-1 sm:mr-2 flex-shrink-0" />
          <span className="whitespace-nowrap">Notifications</span>
        </Button>
      </div>

      {activeTab === 'site' ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Site Title & Subtitle */}
        <div className="glass-card p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-3 bg-orange-500/20 rounded-lg">
              <SettingsIcon className="w-6 h-6 text-orange-400" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">Site Branding</h2>
              <p className="text-sm text-slate-400">Change the site title and subtitle</p>
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <Label htmlFor="new-title" className="text-slate-900 font-semibold">Site Title</Label>
              <Input
                id="new-title"
                data-testid="site-title-input"
                value={newTitle}
                onChange={(e) => {
                  setNewTitle(e.target.value);
                  setHasUnsavedChanges(true);
                }}
                placeholder="Enter site title"
                className="mt-1 bg-white border-2 border-slate-300 text-slate-900 placeholder:text-slate-500 focus:border-blue-500"
              />
            </div>

            <div>
              <Label htmlFor="new-subtitle" className="text-slate-900 font-semibold">Site Subtitle</Label>
              <Input
                id="new-subtitle"
                data-testid="site-subtitle-input"
                value={newSubtitle}
                onChange={(e) => {
                  setNewSubtitle(e.target.value);
                  setHasUnsavedChanges(true);
                }}
                placeholder="Enter site subtitle"
                className="mt-1 bg-white border-2 border-slate-300 text-slate-900 placeholder:text-slate-500 focus:border-blue-500"
              />
            </div>

            <div>
              <Label htmlFor="background-color" className="text-slate-900 font-semibold">Background Color</Label>
              <div className="flex gap-3 mt-1">
                <Input
                  type="color"
                  id="background-color"
                  value={backgroundColor}
                  onChange={(e) => {
                    setBackgroundColor(e.target.value);
                    setHasUnsavedChanges(true);
                  }}
                  className="w-20 h-12 cursor-pointer"
                />
                <Input
                  type="text"
                  value={backgroundColor}
                  onChange={(e) => {
                    setBackgroundColor(e.target.value);
                    setHasUnsavedChanges(true);
                  }}
                  placeholder="#f9fafb"
                  className="flex-1 h-12 bg-white border-2 border-slate-300 text-slate-900 placeholder:text-slate-500 focus:border-blue-500"
                />
              </div>
              <p className="text-xs text-slate-400 mt-1">Choose a color or enter a hex code</p>
            </div>

            <div className="p-3 bg-slate-800/50 rounded-lg">
              <p className="text-xs text-slate-400 mb-1">Current Settings:</p>
              <p className="text-white font-semibold">{siteSettings.site_title}</p>
              <p className="text-slate-400 text-sm">{siteSettings.site_subtitle}</p>
              <div className="flex items-center gap-2 mt-2">
                <div 
                  className="w-6 h-6 rounded border-2 border-slate-600" 
                  style={{ backgroundColor: siteSettings.background_color || '#f9fafb' }}
                ></div>
                <p className="text-slate-400 text-sm">{siteSettings.background_color || '#f9fafb'}</p>
              </div>
            </div>

            <Button
              onClick={handleUpdateSettings}
              disabled={isLoading || (newTitle === siteSettings.site_title && newSubtitle === siteSettings.site_subtitle && backgroundColor === siteSettings.background_color)}
              className="w-full bg-gradient-to-r from-orange-500 to-orange-600"
              data-testid="update-settings-btn"
            >
              <Save className="w-4 h-4 mr-2" />
              Update Settings
            </Button>
          </div>
        </div>

        {/* Site Logo */}
        <div className="glass-card p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-3 bg-blue-500/20 rounded-lg">
              <Upload className="w-6 h-6 text-blue-400 upload-icon-idle" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">Site Logo</h2>
              <p className="text-sm text-slate-400">Upload a custom logo for your site</p>
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <Label className="text-slate-300">Current Logo</Label>
              <div className="mt-2 p-6 bg-slate-800/50 rounded-lg flex items-center justify-center">
                {siteSettings.site_logo ? (
                  <img
                    src={`${BACKEND_URL}${siteSettings.site_logo}`}
                    alt="Site logo"
                    className="max-h-20 object-contain"
                  />
                ) : (
                  <div className="text-center">
                    <p className="text-slate-400 text-4xl font-bold mb-2">{siteSettings.site_title}</p>
                    <p className="text-slate-500 text-sm">No logo uploaded</p>
                  </div>
                )}
              </div>
            </div>

            <div>
              <Label htmlFor="logo-file" className="text-slate-900 font-semibold">Upload New Logo</Label>
              <Input
                id="logo-file"
                data-testid="logo-file-input"
                type="file"
                accept="image/*"
                onChange={(e) => setLogoFile(e.target.files[0])}
                className="mt-1 bg-white border-2 border-slate-300 text-slate-900 file:text-slate-900 focus:border-blue-500"
              />
              {logoFile && (
                <p className="text-sm text-slate-400 mt-2">Selected: {logoFile.name}</p>
              )}
            </div>

            <Button
              onClick={handleUploadLogo}
              disabled={isUploading || !logoFile}
              className="w-full bg-gradient-to-r from-blue-500 to-blue-600"
              data-testid="upload-logo-btn"
            >
              {isUploading ? (
                <>
                  <Upload className="w-4 h-4 mr-2 upload-icon-uploading" />
                  Uploading...
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4 mr-2 upload-icon-idle" />
                  Upload Logo
                </>
              )}
            </Button>
          </div>
        </div>

        {/* Login Background Image */}
        <div className="glass-card p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 bg-blue-500/20 rounded-lg">
              <ImageIcon className="w-5 h-5 text-blue-400" />
            </div>
            <div>
              <h3 className="text-xl font-bold text-white">Login Background Image</h3>
              <p className="text-slate-400 text-sm">Upload a custom background for the login page</p>
            </div>
          </div>

          <div className="space-y-4">
            {siteSettings.login_background && (
              <div className="p-3 bg-slate-800/50 rounded-lg">
                <p className="text-xs text-slate-400 mb-2">Current Background:</p>
                <img 
                  src={`${BACKEND_URL}${siteSettings.login_background}`}
                  alt="Login background preview"
                  className="w-full h-32 object-cover rounded"
                />
              </div>
            )}

            <div>
              <Label htmlFor="login-bg-file" className="text-slate-900 font-semibold">Upload New Background</Label>
              <Input
                id="login-bg-file"
                type="file"
                accept="image/*"
                onChange={(e) => setLoginBgFile(e.target.files[0])}
                className="mt-1 bg-white border-2 border-slate-300 text-slate-900 file:text-slate-900 focus:border-blue-500"
              />
              {loginBgFile && (
                <p className="text-sm text-slate-400 mt-2">Selected: {loginBgFile.name}</p>
              )}
            </div>

            <Button
              onClick={handleUploadLoginBackground}
              disabled={isUploading || !loginBgFile}
              className="w-full bg-gradient-to-r from-blue-500 to-blue-600"
            >
              {isUploading ? (
                <>
                  <Upload className="w-4 h-4 mr-2 upload-icon-uploading" />
                  Uploading...
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4 mr-2 upload-icon-idle" />
                  Upload Background
                </>
              )}
            </Button>
          </div>
        </div>

        {/* Login Sound */}
        <LoginSoundSettings />

        {/* Preview Section */}
        <div className="glass-card p-6 mt-6">
          <h3 className="text-lg font-bold text-white mb-2">Preview</h3>
          <p className="text-slate-400 mb-4">This is how your site will appear to users</p>
          <div className="p-8 bg-slate-800/50 rounded-lg text-center">
            {siteSettings.site_logo ? (
              <img
                src={`${BACKEND_URL}${siteSettings.site_logo}`}
                alt="Preview"
                className="max-h-24 mx-auto mb-4 object-contain"
              />
            ) : (
              <div className="text-5xl font-bold text-white mb-4">{siteSettings.site_title}</div>
            )}
            <p className="text-slate-400">{siteSettings.site_subtitle}</p>
          </div>
        </div>
        </div>
      ) : activeTab === 'categories' ? (
        /* Categories Tab */
        <div className="glass-card p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 bg-blue-500/20 rounded-lg">
              <Folder className="w-5 h-5 text-blue-400" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-white">Project Categories</h2>
              <p className="text-slate-400">Manage categories for project organization</p>
            </div>
          </div>

          <div className="space-y-4 mb-6">
            <div>
              <Label className="text-slate-900 font-semibold">Add New Category</Label>
              <div className="flex gap-2 mt-2">
                <Input
                  value={newCategory}
                  onChange={(e) => setNewCategory(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleAddCategory()}
                  placeholder="Enter category name"
                  className="flex-1 bg-white border-2 border-slate-300 text-slate-900 placeholder:text-slate-500 focus:border-blue-500"
                />
                <Button onClick={handleAddCategory} className="bg-gradient-to-r from-blue-500 to-blue-600">
                  <Plus className="w-4 h-4 mr-2" />
                  Add
                </Button>
              </div>
            </div>

            <div>
              <Label className="text-slate-900 font-semibold mb-2 block">Current Categories</Label>
              <div className="flex flex-wrap gap-2">
                {categories.map((category) => (
                  <div
                    key={category}
                    className="inline-flex items-center gap-2 px-3 py-2 bg-blue-100 border-2 border-blue-300 rounded-lg text-slate-900 font-medium"
                  >
                    <span>{category}</span>
                    <button
                      onClick={() => handleRemoveCategory(category)}
                      className="hover:text-red-600 transition text-slate-700"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
              {categories.length === 0 && (
                <p className="text-slate-500 text-sm mt-2">No categories yet. Add one above!</p>
              )}
            </div>
          </div>

          <Button
            onClick={handleSaveCategories}
            disabled={isLoading}
            className="bg-gradient-to-r from-green-500 to-green-600 min-h-[48px] w-full sm:w-auto"
          >
            <Save className="w-4 h-4 mr-2" />
            {isLoading ? 'Saving...' : 'Save Categories'}
          </Button>
        </div>
      ) : activeTab === 'service-types' ? (
        /* Service Types Tab */
        <div className="glass-card p-6">
          <h2 className="text-2xl font-bold text-white mb-4">Manage Service Types</h2>
          <p className="text-slate-400 mb-6">Add, remove, or modify the available service types for client services.</p>
          
          <div className="space-y-6">
            <div>
              <Label htmlFor="new-service-type" className="text-slate-900 font-semibold mb-2 block">Add New Service Type</Label>
              <div className="flex gap-2">
                <Input
                  id="new-service-type"
                  value={newServiceType}
                  onChange={(e) => setNewServiceType(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleAddServiceType()}
                  placeholder="e.g., Roofing, Plumbing, HVAC"
                  className="flex-1 bg-white border-2 border-slate-300 text-slate-900 placeholder:text-slate-500 focus:border-blue-500"
                />
                <Button onClick={handleAddServiceType} className="bg-gradient-to-r from-blue-500 to-blue-600">
                  <Plus className="w-4 h-4 mr-2" />
                  Add
                </Button>
              </div>
            </div>

            <div>
              <Label className="text-slate-900 font-semibold mb-2 block">Current Service Types ({serviceTypes.length})</Label>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
                {serviceTypes.map((serviceType) => (
                  <div
                    key={serviceType}
                    className="inline-flex items-center justify-between gap-2 px-3 py-2 bg-blue-100 border-2 border-blue-300 rounded-lg text-slate-900 font-medium"
                  >
                    <span className="truncate">{serviceType}</span>
                    <button
                      onClick={() => handleRemoveServiceType(serviceType)}
                      className="hover:text-red-600 transition flex-shrink-0 text-slate-700"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
              {serviceTypes.length === 0 && (
                <p className="text-slate-500 text-sm mt-2">No service types yet. Add one above!</p>
              )}
            </div>
          </div>

          <Button
            onClick={handleSaveServiceTypes}
            disabled={isLoading}
            className="bg-gradient-to-r from-green-500 to-green-600 min-h-[48px] w-full sm:w-auto mt-6"
          >
            <Save className="w-4 h-4 mr-2" />
            {isLoading ? 'Saving...' : 'Save Service Types'}
          </Button>
        </div>
      ) : activeTab === 'permissions' ? (
        /* Permissions Tab */
        <div className="glass-card p-6">
          <h2 className="text-2xl font-bold text-slate-900 mb-4">User Permissions Management</h2>
          <p className="text-slate-700 text-base mb-6">Control what each user can view and do in the system</p>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* User List */}
            <div className="lg:col-span-1">
              <Label className="text-slate-900 font-semibold mb-2 block text-lg">Select User</Label>
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {allUsers.map(u => (
                  <button
                    key={u.id}
                    onClick={() => fetchUserPermissions(u.id)}
                    className={`w-full text-left px-4 py-3 rounded-lg transition border-2 ${
                      selectedUser?.id === u.id
                        ? 'bg-blue-500 text-white border-blue-600'
                        : 'bg-slate-100 text-slate-900 border-slate-300 hover:bg-slate-200 hover:border-blue-400'
                    }`}
                  >
                    <div className="font-bold text-lg">{u.name}</div>
                    <div className="text-sm capitalize opacity-70">{u.role}</div>
                  </button>
                ))}
              </div>
            </div>

            {/* Permissions Matrix */}
            <div className="lg:col-span-2">
              {selectedUser && userPermissions ? (
                <div className="space-y-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-xl font-bold text-slate-900">{selectedUser.name}</h3>
                      <p className="text-sm text-slate-600 capitalize">{selectedUser.role}</p>
                    </div>
                    <Button
                      onClick={handleSavePermissions}
                      disabled={isLoading}
                      className="bg-gradient-to-r from-green-500 to-green-600"
                    >
                      <Save className="w-4 h-4 mr-2" />
                      {isLoading ? 'Saving...' : 'Save Permissions'}
                    </Button>
                  </div>

                  {/* Projects */}
                  <div className="bg-slate-100 p-4 rounded-lg border-2 border-slate-300">
                    <h4 className="font-bold text-slate-900 mb-3">Projects</h4>
                    <div className="grid grid-cols-2 gap-3">
                      {Object.keys(userPermissions.projects || {}).map(action => (
                        <label key={action} className="flex items-center gap-2 text-slate-900 cursor-pointer hover:text-blue-600 transition">
                          <input
                            type="checkbox"
                            checked={userPermissions.projects[action]}
                            onChange={(e) => handlePermissionChange('projects', action, e.target.checked)}
                            className="w-5 h-5 cursor-pointer"
                          />
                          <span className="capitalize font-medium">{action.replace('_', ' ')}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Services */}
                  <div className="bg-slate-100 p-4 rounded-lg border-2 border-slate-300">
                    <h4 className="font-bold text-slate-900 mb-3">Client Services</h4>
                    <div className="grid grid-cols-2 gap-3">
                      {Object.keys(userPermissions.services || {}).map(action => (
                        <label key={action} className="flex items-center gap-2 text-slate-900 cursor-pointer hover:text-blue-600 transition">
                          <input
                            type="checkbox"
                            checked={userPermissions.services[action]}
                            onChange={(e) => handlePermissionChange('services', action, e.target.checked)}
                            className="w-5 h-5 cursor-pointer"
                          />
                          <span className="capitalize font-medium">{action.replace('_', ' ')}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Photos */}
                  <div className="bg-slate-100 p-4 rounded-lg border-2 border-slate-300">
                    <h4 className="font-bold text-slate-900 mb-3">Photos</h4>
                    <div className="grid grid-cols-2 gap-3">
                      {Object.keys(userPermissions.photos || {}).map(action => (
                        <label key={action} className="flex items-center gap-2 text-slate-900 cursor-pointer hover:text-blue-600 transition">
                          <input
                            type="checkbox"
                            checked={userPermissions.photos[action]}
                            onChange={(e) => handlePermissionChange('photos', action, e.target.checked)}
                            className="w-5 h-5 cursor-pointer"
                          />
                          <span className="capitalize font-medium">{action.replace('_', ' ')}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Notes */}
                  <div className="bg-slate-100 p-4 rounded-lg border-2 border-slate-300">
                    <h4 className="font-bold text-slate-900 mb-3">Notes</h4>
                    <div className="grid grid-cols-2 gap-3">
                      {Object.keys(userPermissions.notes || {}).map(action => (
                        <label key={action} className="flex items-center gap-2 text-slate-900 cursor-pointer hover:text-blue-600 transition">
                          <input
                            type="checkbox"
                            checked={userPermissions.notes[action]}
                            onChange={(e) => handlePermissionChange('notes', action, e.target.checked)}
                            className="w-5 h-5 cursor-pointer"
                          />
                          <span className="capitalize font-medium">{action.replace('_', ' ')}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-center h-64 text-slate-600">
                  <div className="text-center">
                    <UserCheck className="w-16 h-16 mx-auto mb-4 text-slate-400" />
                    <p className="font-medium">Select a user to manage their permissions</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      ) : activeTab === 'users' ? (
        /* Users Management Tab (Pending Approvals) */
        <div className="space-y-6">
          {/* Create Supervisor Section */}
          <div className="glass-card p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-3 bg-purple-500/20 rounded-lg">
                <UserPlus className="w-6 h-6 text-purple-400" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-white">Create Supervisor</h2>
                <p className="text-slate-400 text-sm">Supervisors have the same privileges as admins</p>
              </div>
            </div>
            <CreateSupervisorForm />
          </div>

          {/* Pending Users */}
          <div className="glass-card p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-3 bg-orange-500/20 rounded-lg">
                <Clock className="w-6 h-6 text-orange-400" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-white">Pending User Approvals</h2>
                <p className="text-slate-400 text-sm">Review and approve new user registrations</p>
              </div>
            </div>
            
            {pendingUsers.length === 0 ? (
              <div className="text-center py-8">
                <Clock className="w-16 h-16 text-slate-500 mx-auto mb-4" />
                <p className="text-slate-400 text-lg">No pending user registrations</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {pendingUsers.map((pendingUser) => (
                  <div
                    key={pendingUser.id}
                    id={`pending-user-${pendingUser.id}`}
                    data-testid={`pending-user-card-${pendingUser.id}`}
                    className={`bg-slate-800/50 p-6 rounded-lg border ${highlightUserId === pendingUser.id ? 'border-orange-400 ring-2 ring-orange-400 shadow-lg shadow-orange-500/30' : 'border-slate-700'}`}
                  >
                    <div className="mb-4">
                      {/* User Name - Prominent Display */}
                      <div className="mb-3">
                        <div className="inline-block px-4 py-2 bg-gradient-to-r from-purple-600 to-purple-700 rounded-lg shadow-md">
                          <h3 className="text-base font-bold text-white uppercase tracking-wider">
                            {pendingUser.name}
                          </h3>
                        </div>
                      </div>
                      <p className="text-sm text-slate-300 mb-2">{pendingUser.email}</p>
                      <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold bg-blue-500/20 text-blue-400`}>
                        {pendingUser.role.toUpperCase()}
                      </span>
                    </div>
                    
                    <div className="space-y-2 mb-4 text-sm">
                      {pendingUser.phone && (
                        <p className="text-slate-300">📞 {pendingUser.phone}</p>
                      )}
                      {pendingUser.company && (
                        <p className="text-slate-300">🏢 {pendingUser.company}</p>
                      )}
                      {pendingUser.address && (
                        <p className="text-slate-300 line-clamp-1">📍 {pendingUser.address}</p>
                      )}
                      {pendingUser.suggested_by_name && (
                        <div className="bg-amber-500/10 border border-amber-400/30 rounded-lg px-3 py-2" data-testid={`suggested-by-${pendingUser.id}`}>
                          <p className="text-amber-300 font-semibold">
                            💡 Suggested by {pendingUser.suggested_by_name}
                          </p>
                          {pendingUser.suggested_by_email && (
                            <p className="text-amber-200/70 text-xs">{pendingUser.suggested_by_email}</p>
                          )}
                        </div>
                      )}
                      {pendingUser.suggestion_note && (
                        <p className="text-slate-300 italic">
                          Note: "{pendingUser.suggestion_note}"
                        </p>
                      )}
                      <p className="text-slate-400">
                        Registered: {new Date(pendingUser.created_at).toLocaleDateString('en-CA', { timeZone: 'America/Toronto' })}
                      </p>
                    </div>

                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        onClick={() => handleApproveUser(pendingUser.id)}
                        className="flex-1 bg-gradient-to-r from-green-500 to-green-600"
                      >
                        <UserCheck className="w-4 h-4 mr-2" />
                        Approve
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleRejectUser(pendingUser.id)}
                        className="flex-1 border-red-600 text-red-400 hover:bg-red-600 hover:text-white"
                      >
                        <UserX className="w-4 h-4 mr-2" />
                        Reject
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      ) : activeTab === 'invitations' ? (
        /* Invitations Tab */
        <div className="space-y-6">
          {/* Family & Friends invites (client-added members) */}
          <AdminFamilyInvites />

          {/* Invitation Message Editor */}
          <div className="glass-card p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-3 bg-orange-500/20 rounded-lg">
                <Mail className="w-6 h-6 text-orange-400" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-white">Invitation Message</h2>
                <p className="text-slate-400 text-sm">Customize your client invitation message</p>
              </div>
            </div>
            
            <div className="space-y-4">
              <div>
                <Label className="text-slate-700 mb-2 block">Welcome Message</Label>
                <textarea
                  value={invitationMessage}
                  onChange={(e) => setInvitationMessage(e.target.value)}
                  className="w-full p-3 bg-white border border-slate-300 rounded-lg min-h-[150px] focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  style={{ color: '#000000' }}
                  placeholder="Enter your invitation message..."
                />
              </div>
              
              <Button
                onClick={handleSaveInvitationMessage}
                disabled={isLoading}
                className="bg-gradient-to-r from-green-500 to-green-600"
              >
                <Save className="w-4 h-4 mr-2" />
                {isLoading ? 'Saving...' : 'Save Message'}
              </Button>
            </div>
          </div>

          {/* Create Invitations */}
          <div className="glass-card p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-3 bg-blue-500/20 rounded-lg">
                <Plus className="w-6 h-6 text-blue-400" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-white">Create Invitations</h2>
                <p className="text-slate-400 text-sm">Add client emails to generate invitations</p>
              </div>
            </div>
            
            <div className="space-y-4">
              <div>
                <Label className="text-slate-700 mb-2 block">Client Email Addresses</Label>
                <textarea
                  value={invitationEmails}
                  onChange={(e) => setInvitationEmails(e.target.value)}
                  className="w-full p-3 bg-white border border-slate-300 rounded-lg min-h-[100px] focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  style={{ color: '#000000' }}
                  placeholder="Enter email addresses, one per line...&#10;example1@email.com&#10;example2@email.com"
                />
                <p className="text-slate-400 text-xs mt-1">Enter one email per line</p>
              </div>
              
              <div className="space-y-2">
                <Button
                  onClick={handleCreateInvitations}
                  disabled={isGenerating || !invitationEmails.trim()}
                  className="bg-gradient-to-r from-blue-500 to-blue-600 w-full sm:w-auto"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  {isGenerating ? 'Creating...' : 'Create Invitations'}
                </Button>
                
                <div className="flex flex-col sm:flex-row gap-2">
                  <Button
                    onClick={handleCopyInvitation}
                    disabled={!invitationMessage.trim()}
                    className="bg-gradient-to-r from-orange-500 to-orange-600 flex-1"
                  >
                    {isCopied && copiedType === 'html' ? <Check className="w-4 h-4 mr-2" /> : <Copy className="w-4 h-4 mr-2" />}
                    {isCopied && copiedType === 'html' ? 'Copied!' : 'Copy HTML Email'}
                  </Button>
                  
                  <Button
                    onClick={handleCopyPlainText}
                    disabled={!invitationMessage.trim()}
                    className="bg-gradient-to-r from-green-500 to-green-600 flex-1"
                  >
                    {isCopied && copiedType === 'text' ? <Check className="w-4 h-4 mr-2" /> : <Copy className="w-4 h-4 mr-2" />}
                    {isCopied && copiedType === 'text' ? 'Copied!' : 'Copy Plain Text'}
                  </Button>
                  
                  <Button
                    onClick={handleCopySMS}
                    disabled={!invitationMessage.trim()}
                    className="bg-gradient-to-r from-purple-500 to-purple-600 flex-1"
                  >
                    {isCopied && copiedType === 'sms' ? <Check className="w-4 h-4 mr-2" /> : <Copy className="w-4 h-4 mr-2" />}
                    {isCopied && copiedType === 'sms' ? 'Copied!' : 'Copy SMS Text'}
                  </Button>
                </div>
              </div>
            </div>
          </div>

          {/* Invitation History */}
          <div className="glass-card p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-3 bg-purple-500/20 rounded-lg">
                <Clock className="w-6 h-6 text-purple-400" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-white">Invitation History</h2>
                <p className="text-slate-400 text-sm">Track all sent invitations</p>
              </div>
            </div>

            {invitations.length === 0 ? (
              <div className="text-center py-8">
                <Mail className="w-16 h-16 text-slate-500 mx-auto mb-4" />
                <p className="text-slate-400">No invitations sent yet</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-700">
                      <th className="text-left text-slate-300 font-semibold py-3 px-4">Email</th>
                      <th className="text-left text-slate-300 font-semibold py-3 px-4">Status</th>
                      <th className="text-left text-slate-300 font-semibold py-3 px-4">Invited By</th>
                      <th className="text-left text-slate-300 font-semibold py-3 px-4">Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {invitations.map((invitation) => (
                      <tr key={invitation.id} className="border-b border-slate-800 hover:bg-slate-800/30">
                        <td className="py-3 px-4 text-slate-100">{invitation.email}</td>
                        <td className="py-3 px-4">
                          <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold ${
                            invitation.status === 'accepted' 
                              ? 'bg-green-500/20 text-green-400' 
                              : 'bg-yellow-500/20 text-yellow-400'
                          }`}>
                            {invitation.status}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-slate-300">{invitation.invited_by_name}</td>
                        <td className="py-3 px-4 text-slate-400 text-sm">
                          {new Date(invitation.created_at).toLocaleDateString('en-CA', { timeZone: 'America/Toronto' })}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      ) : activeTab === 'splash' ? (
        /* Splash Screen Tab */
        <div className="glass-card p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-3 bg-orange-500/20 rounded-lg">
              <ImageIcon className="w-6 h-6 text-orange-400" />
            </div>
            <div>
              <h3 className="text-xl font-semibold text-white">Splash Screen Settings</h3>
              <p className="text-sm text-slate-400">Customize the loading screen shown when app starts</p>
            </div>
          </div>

          <div className="space-y-6">
            {/* Current Splash Image */}
            {currentSplashImage && (
              <div className="mb-4">
                <Label className="text-white mb-2 block">Current Splash Image</Label>
                <div className="relative w-full h-48 rounded-lg overflow-hidden bg-slate-900">
                  <img 
                    src={currentSplashImage} 
                    alt="Current splash" 
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            )}

            {/* Custom Message */}
            <div>
              <Label htmlFor="splash-message" className="text-white">
                Splash Screen Message
              </Label>
              <p className="text-sm text-slate-400 mb-2">
                Add a custom message like &quot;Merry Christmas&quot; or announcements (leave empty for no message)
              </p>
              <Input
                id="splash-message"
                type="text"
                value={splashMessage}
                onChange={(e) => setSplashMessage(e.target.value)}
                placeholder="e.g., Merry Christmas! 🎄"
                className="mt-2"
                maxLength={100}
              />
              <p className="text-xs text-slate-500 mt-1">
                {splashMessage.length}/100 characters
              </p>
            </div>

            {/* Save Message Button */}
            <Button
              onClick={async () => {
                try {
                  await axios.put(`${API}/splash-screen-settings/message`, {
                    message: splashMessage
                  });
                  toast.success('Splash message updated!');
                  fetchSplashSettings();
                } catch (error) {
                  toast.error('Failed to update message');
                  console.error('Error updating splash message:', error);
                }
              }}
              className="w-full bg-green-600 hover:bg-green-700"
            >
              <Save className="w-4 h-4 mr-2" />
              Save Message
            </Button>

            <div className="border-t border-slate-700 pt-6">
              <Label htmlFor="splash-image" className="text-white">
                Upload Splash Screen Image
              </Label>
              <p className="text-sm text-slate-400 mb-2">
                This image will be shown behind the logo during app loading (3 seconds)
              </p>
              <Input
                id="splash-image"
                type="file"
                accept="image/*"
                onChange={(e) => {
                  setSplashImageFile(e.target.files[0]);
                  setHasUnsavedChanges(true);
                }}
                className="mt-2"
              />
              {splashImageFile && (
                <p className="text-sm text-green-400 mt-2">
                  ✓ New image selected: {splashImageFile.name}
                </p>
              )}
            </div>

            {/* Info Box */}
            <div className="p-4 bg-blue-900/30 border border-blue-700 rounded-lg">
              <p className="text-sm text-blue-200">
                💡 <strong>Tip:</strong> Use a high-quality image (1920x1080 or larger) for best results. 
                The image will be blurred and used as a background behind your logo.
              </p>
            </div>

            {/* Save Button */}
            <Button
              onClick={async () => {
                if (!splashImageFile) {
                  toast.error('Please select an image first');
                  return;
                }

                setIsUploading(true);
                try {
                  const formData = new FormData();
                  formData.append('splash_image', splashImageFile);

                  await axios.post(`${API}/splash-screen-settings`, formData, {
                    headers: { 'Content-Type': 'multipart/form-data' }
                  });

                  toast.success('Splash screen updated!');
                  setSplashImageFile(null);
                  setHasUnsavedChanges(false);
                  
                  // Refresh to get new image
                  const response = await axios.get(`${API}/splash-screen-settings`);
                  if (response.data.splash_image) {
                    setCurrentSplashImage(response.data.splash_image);
                  }
                } catch (error) {
                  console.error('Error updating splash screen:', error);
                  toast.error('Failed to update splash screen');
                } finally {
                  setIsUploading(false);
                }
              }}
              disabled={isUploading || !splashImageFile}
              className="w-full bg-gradient-to-r from-orange-500 to-red-500"
            >
              {isUploading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Uploading...
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4 mr-2" />
                  Upload Splash Image
                </>
              )}
            </Button>
          </div>
        </div>
      ) : activeTab === 'notifications' ? (
        /* Notifications Tab */
        <div className="space-y-6">
          {/* Client Photo View Alerts */}
          <PhotoViewAlertSettings />
          
          {/* Registration Email Notifications */}
          <NotificationEmailSettings user={user} />
          
          {/* Admin Notification Panel */}
          <AdminNotificationPanel user={user} />
          
          {/* Personal Notification Settings */}
          <NotificationSettings user={user} />
        </div>
      ) : null}
    </div>
  );
};

export default Settings;
